import { SLOT_MAX_RANK, type SaveData, type SaveFile } from '../model/types';
import { gearRequestStillValid } from '../server/gearAction';
import { stageToFight } from '../sim/progress';
import { parseAuthRedirect } from './authRedirect';
import { summaryFromClaim, type ClaimPayload } from './claimSummary';
import type { OfflineSummary } from './claim';
import type { AccountState, BackendEvent, FrontierRequest, GearRequest, SaveBackend, SpendRequest } from './localBackend';
import { RemoteError, remoteErrorFromMessage, type RemoteApi } from './remoteApi';
import { deliverFeedback } from './feedbackReport';
import type { FeedbackDraft } from './feedbackReport';
import { flushFeedbackQueue } from './feedbackQueue';
import { carryFarmQueue, loadFarmQueue, saveFarmQueue } from './farmQueue';
import { browserStorage, type StorageLike } from './clock';
import { acceptSaveData, applyClientLoadout, clientLoadoutPatch, starterSaveDocument } from './saveDocument';

const HEARTBEAT_MS = 20_000;

export interface CloudBackendOptions {
  device?: string;
  redirectTo?: string;
  redirectHref?: string;
  /** 0 disables the heartbeat timer. Defaults to 20 seconds. */
  heartbeatMs?: number;
  schedule?: (run: () => void, ms: number) => () => void;
  /** Where the unsent farm batch lives. Defaults to localStorage when the page has it. */
  storage?: StorageLike;
}

export function createCloudBackend(api: RemoteApi, options: CloudBackendOptions = {}): SaveBackend {
  const device = options.device ?? 'web';
  const redirectTo = options.redirectTo ?? '/';
  const heartbeatMs = options.heartbeatMs ?? HEARTBEAT_MS;
  const schedule = options.schedule ?? defaultSchedule;
  const storage = options.storage ?? browserStorage();
  const redirect = parseAuthRedirect(options.redirectHref ?? 'http://localhost/');

  let user: { id: string; isAnonymous: boolean; email: string | null } | null = null;
  let sessionId: string | null = null;
  let memory: SaveFile | null = null;
  let readOnly = false;
  let notice: string | null = null;
  let collision = redirect.collision;
  let prepared = false;
  let pendingSummary: OfflineSummary | null = null;
  let generation = 0;
  let depth = 0;
  let tail: Promise<void> = Promise.resolve();
  /** Last version adopted from a function or RPC response. Jobs read this when they run. */
  let serverVersion = 0;
  let serverData: SaveData | null = null;
  let pendingCommits = 0;
  let commitQueued = false;
  let latestLoadout: SaveData | null = null;
  let stopHeartbeat: (() => void) | null = null;
  const listeners = new Set<(event: BackendEvent) => void>();
  const flushers: Array<() => void> = [];

  function account(): AccountState {
    return {
      mode: 'cloud',
      label: user?.isAnonymous ? 'Guest' : user?.email || 'Google',
      anonymous: user?.isAnonymous ?? true,
      readOnly,
      notice,
      collision,
    };
  }

  function emit(event: BackendEvent): void {
    for (const listener of listeners) listener(event);
  }

  function emitAccount(): void {
    emit({ type: 'account', account: account() });
  }

  function remember(file: SaveFile | null): void {
    if (!file) {
      memory = null;
      serverData = null;
      serverVersion = 0;
      return;
    }
    serverVersion = Number(file.version);
    try {
      serverData = acceptSaveData(file.data);
    } catch {
      serverData = structuredClone(file.data);
    }
    const shown = pendingCommits > 0 && latestLoadout ? applyClientLoadout(serverData, latestLoadout) : serverData;
    memory = { version: serverVersion + pendingCommits, data: structuredClone(shown) };
  }

  function adopt(result: { version: number; data?: SaveData }): SaveFile | null {
    if (!result.data || !Number.isInteger(Number(result.version))) return null;
    remember({ version: Number(result.version), data: result.data });
    return memory ? structuredClone(memory) : null;
  }

  async function reload(reason: 'save_conflict' | 'superseded' | null): Promise<void> {
    pendingCommits = 0;
    latestLoadout = null;
    commitQueued = false;
    remember(await api.readSave());
    if (reason && memory) emit({ type: 'reloaded', file: structuredClone(memory), reason });
  }

  async function refetchQuiet(): Promise<void> {
    remember(await api.readSave());
    if (!memory) throw new RemoteError('save_invalid', 'missing_save');
  }

  function notifyFlush(): void {
    if (depth > 0) return;
    const waiting = flushers.splice(0);
    for (const resolve of waiting) resolve();
  }

  function enqueue<T>(run: () => Promise<T>): Promise<T> {
    depth += 1;
    const job = tail.then(run, run);
    tail = job.then(
      () => {
        depth -= 1;
        if (depth === 0) notifyFlush();
      },
      () => {
        depth -= 1;
        if (depth === 0) notifyFlush();
      },
    );
    return job;
  }

  function stopBeat(): void {
    stopHeartbeat?.();
    stopHeartbeat = null;
  }

  function startBeat(): void {
    stopBeat();
    if (heartbeatMs <= 0 || readOnly || !sessionId) return;
    stopHeartbeat = schedule(() => {
      void beat();
    }, heartbeatMs);
  }

  async function beat(): Promise<void> {
    if (!sessionId || readOnly) return;
    try {
      await api.rpc('heartbeat', { p_session: sessionId });
    } catch (error) {
      if (asRemote(error).code === 'superseded') await becomeReadOnly();
    }
  }

  async function becomeReadOnly(): Promise<void> {
    readOnly = true;
    stopBeat();
    generation += 1;
    pendingCommits = 0;
    latestLoadout = null;
    commitQueued = false;
    try {
      await reload('superseded');
    } catch {
      emitAccount();
      return;
    }
    emitAccount();
  }

  async function commitOnce(): Promise<void> {
    if (!sessionId || !latestLoadout) throw new RemoteError('superseded');
    const result = await api.rpc<{ version: number; data?: SaveData }>('save_commit', {
      p_session: sessionId,
      p_expected_version: serverVersion,
      p_patch: clientLoadoutPatch(latestLoadout),
    });
    pendingCommits = Math.max(0, pendingCommits - 1);
    adopt(result);
  }

  async function writeLoadout(): Promise<void> {
    try {
      await commitOnce();
    } catch (error) {
      const remote = asRemote(error);
      if (remote.code === 'network') {
        try {
          await commitOnce();
          return;
        } catch (again) {
          const second = asRemote(again);
          if (second.code === 'network') {
            generation += 1;
            pendingCommits = 0;
            latestLoadout = null;
            commitQueued = false;
            notice = 'Cloud save failed. Reloaded the server braid.';
            await reload('save_conflict');
            emitAccount();
            return;
          }
          throw second;
        }
      }
      if (remote.code !== 'save_conflict') throw remote;
      await refetchQuiet();
      await commitOnce();
    }
  }

  async function failWrite(remote: RemoteError): Promise<void> {
    generation += 1;
    pendingCommits = 0;
    latestLoadout = null;
    commitQueued = false;
    if (remote.code === 'superseded') {
      await becomeReadOnly();
      return;
    }
    if (remote.code === 'save_conflict') {
      notice = 'That save was out of date. Reloaded the server braid.';
      await reload('save_conflict');
      emitAccount();
      return;
    }
    notice = remote.message;
    emitAccount();
  }

  async function callSim(
    name: string,
    body: Record<string, unknown>,
    stillValid: (data: SaveData) => boolean,
    invokeOptions?: { keepalive?: boolean },
  ): Promise<SaveFile> {
    const gen = generation;
    return enqueue(async () => {
      if (gen !== generation || readOnly || !sessionId || !memory) throw new RemoteError('superseded');
      const send = () =>
        api.invoke<{ version: number; data: SaveData }>(
          name,
          {
            sessionId,
            expectedVersion: serverVersion,
            ...body,
          },
          invokeOptions,
        );
      try {
        const saved = adopt(await send());
        if (!saved) throw new RemoteError('save_invalid');
        return saved;
      } catch (error) {
        const remote = asRemote(error);
        if (remote.code === 'superseded') {
          await becomeReadOnly();
          throw remote;
        }
        if (remote.code !== 'save_conflict') throw remote;
        await refetchQuiet();
        if (!serverData || !stillValid(serverData)) return structuredClone(memory as SaveFile);
        try {
          const saved = adopt(await send());
          if (!saved) throw new RemoteError('save_invalid');
          return saved;
        } catch (again) {
          const second = asRemote(again);
          if (second.code === 'save_conflict') {
            notice = 'That save was out of date. Reloaded the server braid.';
            await reload('save_conflict');
            emitAccount();
          } else if (second.code === 'superseded') {
            await becomeReadOnly();
          }
          throw second;
        }
      }
    });
  }

  async function openSession(): Promise<void> {
    const started = await api.rpc<{ sessionId: string; takeover: boolean }>('start_session', {
      p_device: device,
      p_initial: clientLoadoutPatch(starterSaveDocument()),
    });
    sessionId = started.sessionId;
    readOnly = false;
  }

  /**
   * Send the queue left behind by a closed tab before claim_offline moves the anchor.
   * A rejected batch (save_invalid, or a stale, gapped, or illegal stage) is dropped.
   * Any other failure keeps the queue; the claim still runs, and a later settle is
   * capped to the time the server can see. A replay that accepted fewer wins than
   * the queue now holds carries the remainder onto the next sequence.
   */
  async function sendStoredFarmBatch(): Promise<void> {
    const queue = loadFarmQueue(storage);
    if (!queue || queue.count < 1) return;
    try {
      const saved = await callSim(
        'validate_clear',
        { batch: true, stageId: queue.stageId, count: queue.count, seq: queue.seq },
        () => true,
      );
      const receipt = saved.data.idle.farmBatch?.receipt;
      const latest = loadFarmQueue(storage);
      saveFarmQueue(
        storage,
        carryFarmQueue(
          latest,
          queue,
          receipt ? { seq: receipt.seq, requested: receipt.requested } : null,
        ),
      );
    } catch (error) {
      const code = asRemote(error).code;
      if (code === 'save_invalid' || code === 'batch_stale' || code === 'batch_gap' || code === 'batch_stage') {
        saveFarmQueue(storage, null);
      }
    }
  }

  const backend: SaveBackend = {
    offlineClaims: 'server',
    account,
    load() {
      return memory ? structuredClone(memory) : null;
    },
    commit(expectedVersion, data) {
      if (readOnly) throw new RemoteError('superseded');
      if (!memory || memory.version !== expectedVersion) throw new RemoteError('save_conflict');
      latestLoadout = structuredClone(data);
      if (!commitQueued) {
        commitQueued = true;
        pendingCommits += 1;
        memory = {
          version: serverVersion + pendingCommits,
          data: applyClientLoadout(memory.data, data),
        };
        const gen = generation;
        void enqueue(async () => {
          if (gen !== generation || !latestLoadout) {
            commitQueued = false;
            return;
          }
          commitQueued = false;
          try {
            await writeLoadout();
          } catch (error) {
            await failWrite(asRemote(error));
          }
        });
      } else {
        memory = {
          version: memory.version,
          data: applyClientLoadout(serverData ?? memory.data, data),
        };
      }
      return structuredClone(memory);
    },
    serverNow() {
      return 0;
    },
    advanceClock() {},
    clear() {},
    async prepare() {
      if (prepared) return;
      user = (await api.currentUser()) ?? (await api.signInAnonymously());
      await openSession();
      await reload(null);
      if (!memory) throw new RemoteError('save_invalid', 'missing_save');
      await sendStoredFarmBatch();
      const claim = await api.rpc<ClaimPayload>('claim_offline', { p_session: sessionId });
      await reload(null);
      pendingSummary = summaryFromClaim(claim);
      prepared = true;
      if (api.insertFeedback) {
        try {
          await flushFeedbackQueue((report) => deliverFeedback(api, report));
        } catch {
          // A queued local report stays on this browser until the next sign-in.
        }
      }
      startBeat();
      emitAccount();
    },
    submitFeedback(report: FeedbackDraft) {
      return deliverFeedback(api, report);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async signInWithGoogle() {
      if (user && !user.isAnonymous) return;
      await api.linkGoogle(redirectTo);
    },
    async keepGoogleSave() {
      collision = false;
      await api.signInWithGoogle(redirectTo);
    },
    stayGuest() {
      collision = false;
      emitAccount();
    },
    async signOutAccount() {
      await backend.flush?.();
      if (sessionId) {
        try {
          await api.rpc('end_session', { p_session: sessionId });
        } catch (error) {
          if (asRemote(error).code !== 'superseded') throw asRemote(error);
        }
      }
      stopBeat();
      sessionId = null;
      await api.signOut();
      user = await api.signInAnonymously();
      await openSession();
      await reload(null);
      collision = false;
      notice = null;
      pendingSummary = null;
      startBeat();
      emitAccount();
    },
    async playHere() {
      await openSession();
      await reload(null);
      await sendStoredFarmBatch();
      const claim = await api.rpc<ClaimPayload>('claim_offline', { p_session: sessionId });
      await reload(null);
      pendingSummary = summaryFromClaim(claim);
      notice = null;
      startBeat();
      emitAccount();
      if (memory) emit({ type: 'reloaded', file: structuredClone(memory), reason: 'save_conflict' });
    },
    markAway() {
      const session = sessionId;
      stopBeat();
      sessionId = null;
      if (!session) return;
      void api.rpc('end_session', { p_session: session }).catch(() => undefined);
    },
    async markBack() {
      await openSession();
      await reload(null);
      await sendStoredFarmBatch();
      const claim = await api.rpc<ClaimPayload>('claim_offline', { p_session: sessionId });
      await reload(null);
      startBeat();
      emitAccount();
      return summaryFromClaim(claim);
    },
    consumeOfflineSummary() {
      const summary = pendingSummary;
      pendingSummary = null;
      return summary;
    },
    flush() {
      if (depth === 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        flushers.push(resolve);
      });
    },
    async commitLoadout() {
      await enqueue(async () => {
        if (readOnly || !sessionId || !memory) throw new RemoteError('superseded');
        const result = await api.invoke<{ version: number; data?: SaveData }>('commit_loadout', { sessionId });
        if (result && typeof result === 'object' && 'version' in result) adopt(result);
      });
    },
    validateClear(stageId: number) {
      return callSim('validate_clear', { stageId }, (data) => stageToFight(data.progress) === stageId);
    },
    settleFarmBatch(request, invokeOptions) {
      return callSim(
        'validate_clear',
        { batch: true, stageId: request.stageId, count: request.count, seq: request.seq },
        () => true,
        invokeOptions,
      );
    },
    readFarmQueue() {
      return loadFarmQueue(storage);
    },
    writeFarmQueue(queue) {
      saveFarmQueue(storage, queue);
    },
    applySpend(request: SpendRequest, options?: { keepalive?: boolean }) {
      return callSim('spend_points', { ...request }, (data) => spendRequestStillValid(data, request), options);
    },
    applyFrontier(request: FrontierRequest) {
      return callSim('progress_action', { ...request }, (data) => frontierStillValid(data, request));
    },
    gearAction(request: GearRequest) {
      return callSim('gear_action', { ...request }, (data) => gearRequestStillValid(data, request));
    },
    dispose() {
      stopBeat();
      generation += 1;
      pendingCommits = 0;
      latestLoadout = null;
      commitQueued = false;
    },
  };

  return backend;
}

function defaultSchedule(run: () => void, ms: number): () => void {
  const timer = setInterval(run, ms);
  return () => clearInterval(timer);
}

function frontierStillValid(data: SaveData, request: FrontierRequest): boolean {
  if (request.action === 'push') return data.progress.mode !== 'push' || data.progress.drillStage > 0;
  if (request.action === 'cancel_drill') return data.progress.drillStage > 0;
  return data.progress.drillStage !== request.stageId;
}

/** True only when the whole spend is still legal, so a conflict retry cannot apply it twice. */
export function spendRequestStillValid(data: SaveData, request: SpendRequest): boolean {
  if (request.action === 'rebraid') {
    return Object.values(data.hero.build.ranks).some((rank) => (rank ?? 0) > 0);
  }
  if (request.action === 'spend' && request.ranks) {
    if (request.ranks.length === 0) return false;
    const seen = new Set<string>();
    let need = 0;
    for (const spend of request.ranks) {
      if (seen.has(spend.slot)) return false;
      seen.add(spend.slot);
      if (!Number.isInteger(spend.count) || spend.count < 1) return false;
      const spec = data.hero.build[spend.slot];
      if (!spec) return false;
      const room = SLOT_MAX_RANK[spend.slot] - (data.hero.build.ranks[spec] ?? 0);
      if (spend.count > room) return false;
      need += spend.count;
    }
    return data.hero.unspentPoints >= need;
  }
  if (request.action === 'spend' && request.slot) {
    const spec = data.hero.build[request.slot];
    if (!spec || data.hero.unspentPoints < 1) return false;
    return (data.hero.build.ranks[spec] ?? 0) < SLOT_MAX_RANK[request.slot];
  }
  if (request.action === 'set_spec' && request.slot) {
    return data.hero.build[request.slot] !== (request.spec ?? null);
  }
  return false;
}

function asRemote(error: unknown): RemoteError {
  if (error instanceof RemoteError) return error;
  if (error instanceof Error) return remoteErrorFromMessage(error.message);
  return new RemoteError('unknown');
}
